import {
  FC,
  useState,
  useEffect,
  useRef,
  createContext,
  useContext,
  Dispatch,
  SetStateAction,
} from 'react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {getUserByToken} from './_requests'
import {WithChildren} from '../../../../_metronic/helpers'
import { useErrorHandler } from '../../../../_metronic/helpers/ErrorHandler'
import * as AuthService from "../services/AuthService";
import { mapState } from '../../gis/Map/MapState'
import {useTranslation} from 'react-i18next'
import {createSessionRevalidator} from './sessionRevalidation'

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
  revalidateAuthentication: () => Promise.resolve(false),
}

const AuthContext = createContext(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider = ({children}) => {
  const [auth, setAuth] = useState(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState()
  const authRef = useRef(auth)
  const logoutRef = useRef()
  const mountedRef = useRef(true)
  const revalidatorRef = useRef()
  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])
  const saveAuth = (auth) => {
    authRef.current = auth
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuthSession(currentUser)
    }
  }

  const logout = () => {
    saveAuth(undefined)
    setCurrentUser(undefined)
    mapState.reload();    
  }
  logoutRef.current = logout
  if (!revalidatorRef.current) {
    revalidatorRef.current = createSessionRevalidator({
      getToken: () => {
        const token = authRef.current?.api_token
        return mountedRef.current && token === authHelper.getAuth()?.api_token ? token : undefined
      },
      verifyToken: (token) => AuthService.me(token),
      invalidate: () => logoutRef.current(),
    })
  }

  return (
    <AuthContext.Provider value={{auth, saveAuth, currentUser, setCurrentUser, logout, revalidateAuthentication: revalidatorRef.current}}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit= ({children}) => {
  const {auth, logout, setCurrentUser} = useAuth()
  const {t} = useTranslation()
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  const [bootstrapError, setBootstrapError] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const { errorHandler } = useErrorHandler();
  // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application
  useEffect(() => {
    let cancelled = false
    setShowSplashScreen(true)
    setBootstrapError(false)
    const requestUser = async (apiToken) => {
      try {
        // Only me() can establish that the stored session itself is invalid.
        // The generic handler erases HTTP status and redirects 401/403, which
        // would repeatedly reload login with the same rejected token.
        const request = AuthService.me(apiToken).catch((error) => {
          if (error?.response?.status === 401 || error?.response?.status === 403) {
            if (!cancelled) logout()
            return {ok: false, invalidSession: true}
          }
          throw error
        })
        const res = await errorHandler(request, [401, 403, 422])
        if (!cancelled && res?.ok && res?.data?.data) {
          setCurrentUser(res.data.data)
        } else if (!cancelled && !res?.invalidSession) {
          setBootstrapError(true)
        }
      } catch (error) {
        if (!cancelled) setBootstrapError(true)
      } finally {
        if (!cancelled) setShowSplashScreen(false)
      }
    }

    if (auth && auth.api_token) {
      requestUser(auth.api_token)
    } else {
      logout()
      setShowSplashScreen(false)
    }
    return () => { cancelled = true }
    // eslint-disable-next-line
  }, [attempt])

  if (showSplashScreen) return <LayoutSplashScreen />
  if (bootstrapError) {
    return <div className="d-flex flex-column align-items-center justify-content-center p-6" role="alert">
      <p>{t('auth:session_validation_error', {defaultValue: 'No se pudo verificar la sesión. Tus credenciales se conservaron; reintentá cuando vuelva la conexión.'})}</p>
      <button type="button" className="btn btn-primary" onClick={() => setAttempt((value) => value + 1)}>
        {t('common:actions.retry', {defaultValue: 'Reintentar'})}
      </button>
    </div>
  }
  return <>{children}</>
}

export {AuthProvider, AuthInit, useAuth}
